import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Fragment } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createSequence } from '@olivierpascal/helpers';
import { Badge, IOmit } from '@sixui/ui';

import {
  INavigationRailDestinationProps,
  NavigationRailDestination,
} from '~/components/atoms/NavigationRailDestination/NavigationRailDestination';
import { useUserQuery } from '~/lib/api/modules/Users/hooks/useUserQuery';
import { NavigationRailSectionItemSkeleton } from './NavigationRailSectionItemSkeleton';

const DEFAULT_SKELETON_COUNT = 4;

export type INavigationRailSectionProps = {
  items?: Array<INavigationRailSectionItem>;
  loading?: boolean;
  children?: React.ReactNode;
};

export type INavigationRailSectionItem = IOmit<
  INavigationRailDestinationProps,
  'icon' | 'activeIcon'
> & {
  faIcon: IconDefinition;
  faActiveIcon?: IconDefinition;
  proFeature?: boolean;
};

const renderItem = (
  item: INavigationRailSectionItem,
  isSuperAdmin?: boolean,
): JSX.Element => {
  const { faIcon, faActiveIcon, proFeature, ...other } = item;

  return (
    <NavigationRailDestination
      icon={<FontAwesomeIcon icon={faIcon} fixedWidth />}
      activeIcon={
        faActiveIcon ? <FontAwesomeIcon icon={faActiveIcon} fixedWidth /> : null
      }
      badge={proFeature ? <Badge value="Pro" /> : undefined}
      disabled={proFeature && !isSuperAdmin}
      {...other}
    />
  );
};

export const NavigationRailSection: React.FC<INavigationRailSectionProps> = (
  props,
) => {
  const { items, loading, children } = props;
  const userQuery = useUserQuery({});
  const isSuperAdmin = userQuery.data?.superAdmin;

  if (!items && !loading) {
    return null;
  }

  return (
    <>
      {loading
        ? createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
            <NavigationRailSectionItemSkeleton key={index} />
          ))
        : items?.map((item, itemIndex) => (
            <Fragment key={itemIndex}>
              {renderItem(item, isSuperAdmin)}
            </Fragment>
          ))}

      {children}
    </>
  );
};
